/**
 * Proje genelinde satır / blok ekleme ve silme butonları için ortak stil.
 *
 *   <AddRowButton onClick={addCarrier} tooltip="Add carrier" />              // sadece ikon
 *   <AddRowButton onClick={addCargoDetail} label="Add Cargo" />              // ikon + metin
 *   <RemoveRowButton onClick={() => removeCarrier(index)} tooltip="Remove carrier" />
 *
 * Tooltip ile sarmalanabilir (ref ve ek prop'lar köke iletilir). Butonlar type="button"dır,
 * form içinde yanlışlıkla submit tetiklemez.
 */
import React, { forwardRef } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import { useMaterialUIController } from "context";

const ACCENT = "#1A73E8";
const DANGER = "#e53935";

const useTokens = () => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    return darkMode
        ? {
            addBg: "rgba(26, 115, 232, 0.16)",
            addBgHover: "rgba(26, 115, 232, 0.26)",
            addBorder: "rgba(124, 180, 255, 0.35)",
            addColor: "#7cb4ff",
            removeBg: "rgba(229, 57, 53, 0.14)",
            removeBorder: "rgba(248, 113, 113, 0.35)",
            removeColor: "#f87171",
            removeBgHover: "rgba(229, 57, 53, 0.26)",
        }
        : {
            addBg: "rgba(26, 115, 232, 0.08)",
            addBgHover: "rgba(26, 115, 232, 0.16)",
            addBorder: "rgba(26, 115, 232, 0.28)",
            addColor: ACCENT,
            removeBg: "rgba(229, 57, 53, 0.08)",
            removeBorder: "rgba(229, 57, 53, 0.28)",
            removeColor: DANGER,
            removeBgHover: "rgba(229, 57, 53, 0.16)",
        };
};

const baseSx = {
    height: 32,
    minWidth: 32,
    borderRadius: "8px",
    borderStyle: "solid",
    borderWidth: "1px",
    fontFamily: "inherit",
    fontSize: "0.8125rem",
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "background-color 150ms ease, border-color 150ms ease, color 150ms ease",
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
    "&.Mui-disabled": { opacity: 0.45, cursor: "not-allowed", pointerEvents: "auto" },
    // Ekle / sil butonları yan yana geldiğinde bitişik durmasın (kapsayıcının gap değerinden bağımsız)
    "& + .row-action-btn": { marginLeft: "10px" },
};

const withTooltip = (title, node, disabled) =>
    title && !disabled ? (
        <Tooltip title={title} arrow placement="top">
            {node}
        </Tooltip>
    ) : (
        node
    );

export const AddRowButton = forwardRef(function AddRowButton(
    { label, tooltip, onClick, disabled = false, sx, ...rest },
    ref
) {
    const t = useTokens();
    const button = (
        <ButtonBase
            ref={ref}
            type="button"
            className="row-action-btn"
            onClick={onClick}
            disabled={disabled}
            aria-label={label || tooltip || "Add"}
            sx={{
                ...baseSx,
                px: label ? 1.5 : 0,
                width: label ? "auto" : 32,
                gap: 0.75,
                color: t.addColor,
                backgroundColor: t.addBg,
                borderColor: t.addBorder,
                "&:hover": { backgroundColor: t.addBgHover },
                ...sx,
            }}
            {...rest}
        >
            <Icon sx={{ fontSize: "18px !important" }}>add</Icon>
            {label}
        </ButtonBase>
    );
    return withTooltip(label ? null : tooltip || "Add", button, disabled);
});

export const RemoveRowButton = forwardRef(function RemoveRowButton(
    { label, tooltip, onClick, disabled = false, sx, ...rest },
    ref
) {
    const t = useTokens();
    const button = (
        <ButtonBase
            ref={ref}
            type="button"
            className="row-action-btn"
            onClick={onClick}
            disabled={disabled}
            aria-label={label || tooltip || "Remove"}
            sx={{
                ...baseSx,
                px: label ? 1.5 : 0,
                width: label ? "auto" : 32,
                gap: 0.75,
                color: t.removeColor,
                backgroundColor: t.removeBg,
                borderColor: t.removeBorder,
                "&:hover": { backgroundColor: t.removeBgHover, borderColor: t.removeColor },
                ...sx,
            }}
            {...rest}
        >
            <Icon sx={{ fontSize: "18px !important" }}>delete_outline</Icon>
            {label}
        </ButtonBase>
    );
    return withTooltip(label ? null : tooltip || "Remove", button, disabled);
});
